// Skripsi utilities for generating outline and managing content
import linkupService from '../../services/linkupService'
import { MODEL_MODES } from '../../constants/agentConfig'

// Generate skripsi outline based on query/topic using LLM
export const generateSkripsiOutline = async (topic, llmService) => {
  let academicContext = ''
  let academicSources = []
  
  try {
    // Lakukan pencarian akademik untuk mendapatkan konteks yang relevan
    console.log(`🔍 Mencari konteks akademik untuk topik: ${topic}`);
    const academicResults = await linkupService.getScientificInfo(`${topic} research methodology academic structure`);
    
    if (academicResults && academicResults.sources && academicResults.sources.length > 0) {
      academicSources = academicResults.sources.slice(0, 40); // Simpan 20-40 sumber untuk digunakan dalam skripsi
      const topSources = academicSources.slice(0, 5);
      academicContext = `\n\nKonteks Akademik Referensi:\n${topSources.map((source, index) => 
        `${index + 1}. ${source.title}\n   ${source.snippet}`
      ).join('\n')}`;
      console.log(`✅ Ditemukan ${academicSources.length} sumber akademik untuk outline`);
    }
  } catch (error) {
    console.warn('❌ Error saat mencari konteks akademik:', error.message);
  }
  
  const prompt = `Buatkan OUTLINE (struktur poin-poin) skripsi untuk topik "${topic}" dalam format JSON dengan struktur berikut:
  {
    "sections": [
      {
        "id": "bab1_pendahuluan",
        "title": "BAB I PENDAHULUAN",
        "description": "Deskripsi singkat isi bagian",
        "minWords": 2000,
        "isEditable": true,
        "subsections": ["Latar Belakang", "Rumusan Masalah", "Tujuan Penelitian"]
      }
    ]
  }
  
  PENTING: Ini adalah OUTLINE (kerangka/struktur), BUKAN konten lengkap skripsi!
  
  WAJIB SERTAKAN BAGIAN-BAGIAN BERIKUT (TIDAK BOLEH DIHILANGKAN):
  1. BAB I PENDAHULUAN
  2. BAB II TINJAUAN PUSTAKA
  3. BAB III METODOLOGI PENELITIAN
  4. BAB IV HASIL DAN PEMBAHASAN
  5. BAB V PENUTUP
  6. DAFTAR PUSTAKA (id: "daftar_pustaka")
  7. LAMPIRAN (id: "lampiran")
  
  Pedoman:
  - Ikuti struktur skripsi Indonesia standar
  - Berikan ID unik untuk setiap bagian (contoh: bab1_pendahuluan, bab2_tinjauan_pustaka, daftar_pustaka, lampiran)
  - Sertakan subsections (sub-bagian) untuk setiap bab
  - Description berisi penjelasan singkat apa yang akan dibahas di bagian tersebut
  - Sesuaikan dengan topik: "${topic}"
  - PASTIKAN Daftar Pustaka dan Lampiran SELALU ada di akhir outline
  - Pertimbangkan konteks akademik yang relevan jika tersedia${academicContext}
  
  Berikan HANYA JSON tanpa penjelasan tambahan:`
  
  try {
    // Use GENIUS mode for comprehensive academic writing
    const response = await llmService.sendMessage(
      [{ role: 'user', content: prompt }],
      { 
        mode: MODEL_MODES.GENIUS,
        searchType: 'academic',
        stream: false,
        consumeCredits: false // Don't consume credits for outline generation
      }
    )
    const cleanedResponse = response.replace(/```json|```/g, '').trim()
    const outline = JSON.parse(cleanedResponse)
    
    if (outline && outline.sections && Array.isArray(outline.sections)) {
      // Pastikan setiap section memiliki ID dan struktur yang benar
      let processedSections = outline.sections.map((section, index) => ({
        id: section.id || `section_${index + 1}`,
        title: section.title,
        description: section.description || '',
        minWords: section.minWords || 2000,
        isEditable: section.isEditable !== false,
        subsections: section.subsections || [],
        topic: topic
      }))
      
      // Validasi dan tambahkan bagian wajib jika tidak ada
      const requiredSections = [
        {
          id: 'daftar_pustaka',
          title: 'DAFTAR PUSTAKA',
          minWords: 0,
          description: 'Daftar referensi dan sumber yang digunakan dalam penelitian',
          isEditable: false
        },
        {
          id: 'lampiran',
          title: 'LAMPIRAN',
          minWords: 0,
          description: 'Dokumen pendukung, data mentah, dan materi tambahan',
          isEditable: true
        }
      ]
      
      // Periksa dan tambahkan bagian yang hilang
      requiredSections.forEach(requiredSection => {
        const exists = processedSections.some(section => section.id === requiredSection.id)
        if (!exists) {
          console.log(`⚠️ Menambahkan bagian yang hilang: ${requiredSection.title}`)
          processedSections.push({
            ...requiredSection,
            topic: topic
          })
        }
      })
      
      return {
        outline: processedSections,
        academicSources: academicSources
      }
    } else {
      throw new Error('Format outline tidak valid')
    }
  } catch (error) {
    console.warn('❌ Error generating outline with LLM, using fallback:', error.message);
    // Fallback ke outline default jika LLM gagal
    return {
      outline: getDefaultOutline(topic),
      academicSources: academicSources
    }
  }
}

// Fallback outline function
const getDefaultOutline = (topic) => {
  const baseOutline = [
    {
      id: 'abstract',
      title: 'Abstrak',
      minWords: 200,
      description: 'Ringkasan penelitian dalam bahasa Indonesia',
      isEditable: true
    },
    {
      id: 'abstract_en',
      title: 'Abstract (English)',
      minWords: 200,
      description: 'Ringkasan penelitian dalam bahasa Inggris',
      isEditable: true
    },
    {
      id: 'kata_pengantar',
      title: 'Kata Pengantar',
      minWords: 300,
      description: 'Ucapan terima kasih dan pengantar dari penulis',
      isEditable: true
    },
    {
      id: 'daftar_isi',
      title: 'Daftar Isi',
      minWords: 0,
      description: 'Daftar isi dengan nomor halaman',
      isEditable: false
    },
    {
      id: 'daftar_tabel',
      title: 'Daftar Tabel',
      minWords: 0,
      description: 'Daftar tabel yang digunakan dalam skripsi',
      isEditable: true
    },
    {
      id: 'daftar_gambar',
      title: 'Daftar Gambar',
      minWords: 0,
      description: 'Daftar gambar dan ilustrasi dalam skripsi',
      isEditable: true
    },
    {
      id: 'bab1_pendahuluan',
      title: 'BAB I PENDAHULUAN',
      minWords: 4000,
      description: 'Latar belakang, rumusan masalah, tujuan, manfaat penelitian',
      isEditable: true,
      subsections: [
        'Latar Belakang',
        'Rumusan Masalah',
        'Tujuan Penelitian',
        'Manfaat Penelitian',
        'Batasan Masalah',
        'Sistematika Penulisan'
      ]
    },
    {
      id: 'bab2_tinjauan_pustaka',
      title: 'BAB II TINJAUAN PUSTAKA',
      minWords: 8000,
      description: 'Landasan teori, penelitian terdahulu, kerangka pemikiran',
      isEditable: true,
      subsections: [
        'Landasan Teori',
        'Penelitian Terdahulu',
        'Kerangka Pemikiran',
        'Hipotesis (jika ada)'
      ]
    },
    {
      id: 'bab3_metodologi',
      title: 'BAB III METODOLOGI PENELITIAN',
      minWords: 4000,
      description: 'Metode penelitian, populasi, sampel, teknik pengumpulan data',
      isEditable: true,
      subsections: [
        'Jenis Penelitian',
        'Lokasi dan Waktu Penelitian',
        'Populasi dan Sampel',
        'Teknik Pengumpulan Data',
        'Instrumen Penelitian',
        'Teknik Analisis Data'
      ]
    },
    {
      id: 'bab4_hasil_pembahasan',
      title: 'BAB IV HASIL DAN PEMBAHASAN',
      minWords: 15000,
      description: 'Hasil penelitian, analisis data, pembahasan temuan',
      isEditable: true,
      subsections: [
        'Gambaran Umum Objek Penelitian',
        'Hasil Penelitian',
        'Analisis Data',
        'Pembahasan',
        'Implikasi Hasil Penelitian'
      ]
    },
    {
      id: 'bab5_penutup',
      title: 'BAB V PENUTUP',
      minWords: 3000,
      description: 'Kesimpulan, saran, dan keterbatasan penelitian',
      isEditable: true,
      subsections: [
        'Kesimpulan',
        'Saran',
        'Keterbatasan Penelitian'
      ]
    },
    {
      id: 'daftar_pustaka',
      title: 'DAFTAR PUSTAKA',
      minWords: 0,
      description: 'Daftar referensi dan sumber yang digunakan dalam penelitian',
      isEditable: false
    },
    {
      id: 'lampiran',
      title: 'LAMPIRAN',
      minWords: 0,
      description: 'Dokumen pendukung, data mentah, dan materi tambahan',
      isEditable: true
    }
  ]

  // Customize outline based on topic if needed
  return baseOutline.map(section => ({
    ...section,
    topic: topic
  }))
}

// Calculate total words from outline
export const calculateTotalWords = (outline) => {
  return outline.reduce((total, section) => total + (section.minWords || 0), 0)
}

// Validate outline meets minimum requirements
export const validateOutline = (outline) => {
  const totalWords = calculateTotalWords(outline)
  const requiredSections = [
    'bab1_pendahuluan', 
    'bab2_tinjauan_pustaka', 
    'bab3_metodologi', 
    'bab4_hasil_pembahasan', 
    'bab5_penutup',
    'daftar_pustaka',
    'lampiran'
  ]
  const missingSections = requiredSections.filter(reqId => 
    !outline.some(section => section.id === reqId)
  )
  
  return {
    isValid: totalWords >= 30000 && missingSections.length === 0,
    totalWords,
    missingSections,
    errors: [
      ...(totalWords < 30000 ? [`Minimal 30.000 kata diperlukan (saat ini: ${totalWords.toLocaleString()})`] : []),
      ...(missingSections.length > 0 ? [`Bagian wajib yang hilang: ${missingSections.join(', ')}`] : [])
    ]
  }
}

// Parse markdown content and extract citations
export const parseMarkdownContent = (content) => {
  if (!content) return { parsedContent: '', citations: [] }
  
  // Extract APA citations from content
  const citationRegex = /\(([^)]+,\s*\d{4}[a-z]?)\)/g
  const citations = []
  let match
  
  while ((match = citationRegex.exec(content)) !== null) {
    citations.push(match[1])
  }
  
  // Remove duplicate citations
  const uniqueCitations = [...new Set(citations)]
  
  return {
    parsedContent: content,
    citations: uniqueCitations
  }
}

// Format academic sources to APA style
export const formatSourceToAPA = (source, index) => {
  const year = new Date().getFullYear() // Fallback year if not available
  const title = source.title || 'Untitled'
  const url = source.url || '#'
  
  // Extract author from title or use generic format
  const authorMatch = title.match(/^([^.]+)\./)
  const author = authorMatch ? authorMatch[1] : 'Author'
  
  return {
    id: `ref_${index + 1}`,
    citation: `${author} (${year})`,
    fullReference: `${author}. (${year}). ${title}. Retrieved from ${url}`,
    url: url,
    title: title
  }
}

// Count words in text content
export const countWords = (text) => {
  if (!text || typeof text !== 'string') return 0
  return text.trim().split(/\s+/).filter(word => word.length > 0).length
}

// Format progress percentage
export const formatProgress = (current, total) => {
  if (total === 0) return 0
  return Math.round((current / total) * 100)
}

// Estimate time remaining
export const estimateTimeRemaining = (current, total, startTime) => {
  if (current === 0) return null
  
  const elapsed = Date.now() - startTime
  const avgTimePerSection = elapsed / current
  const remaining = (total - current) * avgTimePerSection
  
  const minutes = Math.ceil(remaining / (1000 * 60))
  
  if (minutes < 1) return 'Kurang dari 1 menit'
  if (minutes === 1) return '1 menit'
  if (minutes < 60) return `${minutes} menit`
  
  const hours = Math.floor(minutes / 60)
  const remainingMinutes = minutes % 60
  
  if (hours === 1 && remainingMinutes === 0) return '1 jam'
  if (hours === 1) return `1 jam ${remainingMinutes} menit`
  if (remainingMinutes === 0) return `${hours} jam`
  
  return `${hours} jam ${remainingMinutes} menit`
}